import { GridWidgetTask } from './grid-widget-task';
import { FilterContainer } from './filter-container';
import { GridWidgetDataField, DisableFilterOption } from './grid-widget-data-field';
import { GridWidgetFilter } from './grid-widget-filter';
import { OrderedMap } from '../ordered-map';
import { pdsUtils } from '../../pds-utils';
import { RestArgument } from './rest-argument';
import { InverseMultiSelectField } from './inverse-multi-select-field';
import { InverseMultiSelectFilter } from './inverse-multi-select-filter';

interface FilterState {
  fieldId: string;
  value: Object | undefined;
}

export class FilterTask extends GridWidgetTask {
  /**
   * The name of the task. Something like 'Filter', 'Paginate', or 'Sort'
   */
  name = 'filter';

  /**
   * The results of the last run will be stored here.
   */
  result: Array<any>;

  //Array of GridWidgetFilter objects. These show up in the UI. One for each row
  //These are the filters use for filtering in the run method.
  filterContainers: Array<FilterContainer> = [];

  //The number of filterContainers the user can add.
  //Gets updated as filterContainers are added, removed, or operators change.
  //This is what drives the ng-disabled on the 'add another filter' button in the filter controls.
  availableFiltersCount = 0;

  //A map of DataField objects
  //These are the fields that could show up in the filter drop downs,
  //provided they aren't already in use,
  //for the customer to decide what field to search.
  //NOTE:
  //This is cloned for each GridWidgetFilterContainer created; The clones
  //actually determine what shows up in the DOM.
  //The number of values in this object does not vary
  //
  //NOTE 2:
  //This is something passed in by the grid widget and is expected to change under the hood.
  filterFields: OrderedMap<GridWidgetDataField>;

  /**
   * When this is true, all the filters will show and will not be added to or removed from.  They will just all be there.
   */
  isAllFiltersMode = false;

  constructor(
    filterFields: OrderedMap<GridWidgetDataField>
  ) {
    super();
    this.filterFields = filterFields;
    filterFields.forEach((dataField: GridWidgetDataField) => {
      if (dataField.filterDisabled !== DisableFilterOption.advancedOnly &&
        dataField.filterDisabled !== DisableFilterOption.both) {
        this.availableFiltersCount++;
      }
    });
  }

  getPriority(): number {
    return GridWidgetTask.FILTER_TASK_PRIORITY;
  }

  isFiltersApplied(): boolean {
    return this.filterContainers.length > 1 ||
      (this.filterContainers.length > 0 && !this.filterContainers[0].filter.isBlank());
  }
  /**
   * This is called when a customer changes the 'type' of field to filter in
   * the GridWidgetFilterContainer row.
   *
   * @param updatedFilterContainer The FilterContainer that gets updated.
   * @param selectedFieldId: The fieldId to set into the filter container.
   * The template pushes in the selectedFieldId when the select option is changed.
   * When a new filter is added, the selectedFieldId is already set so it is not passed in.
   */
  filterFieldUpdated(updatedFilterContainer: FilterContainer, selectedFieldId?): void {
    if (selectedFieldId != null) {
      updatedFilterContainer.selectedFieldId = selectedFieldId;
    }

    let lastDataField = updatedFilterContainer.update();

    //Add the lastDataField back into the pool for each of the available filter fields
    //because it isn't being used anymore.
    if (lastDataField.objectLookupKey !== updatedFilterContainer.dataField.objectLookupKey) {
      this.addToFilterContainers(lastDataField, updatedFilterContainer);
    }

    //Remove the newly selected dataField from everything else
    this.removeFromFilterContainers(updatedFilterContainer.dataField, updatedFilterContainer);
  }

  /**
   * @param fieldId - string - The key of the dataField you want to have the GridWidgetFilterContainer default to.  If
   * undefined is passed in then the GridWidgetFilterContainer will choose the first DataField it has available. FieldId
   * used to be used by the gridWidget but now it's just something handy for unit testing, or controlling the GridWidget
   * manually.
   * NOTE: If the fieldId is passed in and not available, no filter is added.
   * @returns The FilterContainer that just got added.
   */
  addFilter(fieldId?: string): FilterContainer | undefined {
    let availableFieldsMap: { [dataFieldId: string]: GridWidgetDataField } = this.getAvailableFilterFields();
    let availableFieldIds = Object.keys(availableFieldsMap);
    if (availableFieldIds.length > 0 && (!fieldId || availableFieldIds.indexOf(fieldId) > -1)) {
      let filterContainer = new FilterContainer(availableFieldsMap, fieldId);

      this.filterContainers.unshift(filterContainer);
      this.filterFieldUpdated(filterContainer);

      //Now that there is exactly one field per FilterContainer, we can just do one less than the previous
      //number of available fields.
      this.availableFiltersCount = availableFieldIds.length - 1;

      //Now return the filter that just got added.
      return filterContainer;
    }
  }

  /**
   * Wraps addFilter.  Adds default filters when applicable. Then passes through to addFilter.
   */
  addAnotherFilter(): void {
    if (this.filterContainers.length > 0) {
      this.addFilter();
    } else {
      //Add the fixed default filters (back in)
      this.filterFields.forEach((filterField: GridWidgetDataField) => {
        if (filterField.defaultFilterOption) {
          this.addFilter(filterField.id);
        }
      });

      //If nothing else got added, then add one by default.
      if (this.filterContainers.length === 0) {
        this.addFilter();
      }
    }
  }

  /**
   * Removes the filter container from the filter task. Called when the user presses the minus '-' button.
   * @param index: number
   */
  removeFilter(index: number): void {
    if (index >= 0 && index < this.filterContainers.length) {
      // let removedFilter = this.filterContainers.splice(index, 1)[0];

      //this.secondFieldUnSelected(removedFilter.dataField);
      //give the the filterField back to the pool of available filter fields.
      this.addToFilterContainers(this.filterContainers[index].dataField);
      this.availableFiltersCount++;
      this.filterContainers.splice(index, 1);
    }
  }

  /**
   * Removes all GridWidgetFilter objects and then adds the default filterContainer back in
   * with a default value.
   */
  reset(): void {
    this.clear();
    if (this.isAllFiltersMode) {
      this.addAllFilters();
    } else {
      //This is the default path
      this.addAnotherFilter();
    }
  }

  /**
   * Removes all GridWidgetFilter objects and resets the availableFiltersCount to equal
   * the total number of available filters.
   */
  clear(): void {
    //Since we are clearing there will now be a ton of available filters.
    //Let's update that number before clearing the filter containers.
    this.availableFiltersCount += this.filterContainers.length;

    //Clear the advanced Filters.
    //Remove all elements
    this.filterContainers.length = 0;
  }

  /**
   * preRunSetup is intended to run on each filter task, whether the task is gridWidgetTaskActive is set to true or false.
   * This is intended to make each filter object prepare its self for running passesFilter() over and over and over again.
   * Even if the run method is not called, valuable information can be extracted from the prepared filter. Namely for ServerSide mode.
   */
  preRunSetup(): void {
    //this.quickFilters = this.generateQuickFilters();

    //Advanced Filters
    if (this.filterContainers != null && this.filterContainers.length > 0) {
      this.filterContainers.forEach((filterContainer: FilterContainer) => {
        if (filterContainer.filter != null) {
          filterContainer.filter.preFilterUpdate();
        }
      });
    }

    if (!this.isGridWidgetTaskActive()) {
      let filterData: Array<Array<RestArgument>> = [];
      this.filterContainers.forEach((filterContainer: FilterContainer) => {
        filterData.push(filterContainer.filter.getRestArguments());
      });

      this.restArguments = pdsUtils.flatten(filterData);
    }
  }

  /**
   * This is more expensive than contextual changes within the object its self.
   * Only use it from outside.
   */
  updateAvailableFiltersCount(): void {
    this.availableFiltersCount = Object.keys(this.getAvailableFilterFields()).length;
  }

  /**
   * The main function of the Filter Task.
   * Actually filters the data coming through it.
   */
  run(dataRows: Array<any>): Array<any> {
    let filteredData: Array<any> = [];

    if (dataRows != null) {

      //Inverse filters tend to filter things out even when there is no filter instance in the filterContainers.
      dataRows = this.doInverseFilters(dataRows);

      dataRows.forEach((dataRow) => {
        let simpleFilterPassed = true;
        let advancedFiltersPassed = true;

        if (this.filterContainers != null && this.filterContainers.length > 0) {
          //Only specific filters have been activated.
          //find will get the first occurrence of a failure to pass
          //If nothing fails to pass the filter, it will return 'undefined'
          let fails = this.filterContainers.find(function (gwFilterObj: FilterContainer): boolean {
            return !gwFilterObj.passesFilter(dataRow);
          });
          advancedFiltersPassed = (fails == null);
        }

        if (simpleFilterPassed && advancedFiltersPassed) {
          filteredData[filteredData.length] = dataRow;
        }
      });
    }

    return filteredData;
  }

  /**
   * You MUST be able to call JSON.stringify on the returned object.
   */
  getPersistenceObject(): Object | undefined {
    let states: Array<FilterState> = [];

    for (let fc of this.filterContainers) {
      if (!fc.filter.isBlank()) {
        states.push({
          fieldId: fc.filter.dataField.id,
          value: fc.filter.getPersistenceObject()
        });
      }
    }

    if (states.length > 0) {
      return states;
    }
  }

  restoreFromPersistenceObject(value: Object | undefined): void {

    //Whether this thing restores or not, it needs to clear.
    this.clear();

    if (this.isAllFiltersMode) {
      this.addAllFilters();
    }

    if (value == null || !(value instanceof Array)) {
      return;
    }

    let states: Array<FilterState> = <Array<FilterState>>value;

    for (let idx = states.length - 1; idx >= 0; idx--) {
      let state = states[idx];
      if (state.value != null) {
        let filterField = this.filterFields.get(state.fieldId);
        if (filterField) {
          let filterContainer: FilterContainer | undefined;
          if (this.isAllFiltersMode) {
            //In allFiltersMode the filter containers have already been added in the correct order.
            //We just need to find the correct container for the current fieldID and then restore the
            //data into that filter.
            filterContainer = this.filterContainers.find((filterContainer: FilterContainer) => {
              return filterContainer.dataField.id === state.fieldId;
            });
          } else {
            filterContainer = this.addFilter(state.fieldId);
          }
          if (filterContainer != null) {
            filterContainer.filter.restoreFromPersistenceObject(state.value);
          }
        }
      }
    }
  }

  /**
   * After the filter containers are cleared out, this will go through and add
   * a FilterContainer for each of the filterFields.
   */
  addAllFilters(): void {
    for (let i = this.filterFields.array.length - 1; i >= 0; i--) {
      this.addFilter(this.filterFields.array[i].value.id);
    }
  }

  getSelectableFilterFields(): Array<GridWidgetDataField> {

    let selectableFilterFields: Array<GridWidgetDataField> = [];

    if (this.filterContainers != null && this.filterContainers.length > 0) {
      let firstFilterContainer: FilterContainer = this.filterContainers[0];
      this.filterFields.forEach((dataField: GridWidgetDataField) => {
        if (firstFilterContainer.availableFilterFields[dataField.id] != null) {
          selectableFilterFields.push(dataField);
        }
      });
    }

    return selectableFilterFields;
  }

  /**
   * This is a filter function that returns a filtered subset of the passed in array.
   * It runs these filters whether they are in the filterContainers array or not.
   * @param dataRows -> Input
   * @returns {Array<any>} -> Filtered Input
   */
  private doInverseFilters(dataRows: Array<any>): Array<any> {
    this.filterFields.forEach((dField: GridWidgetDataField, key: any, index: number) => {
      if (dField instanceof InverseMultiSelectField) {
        let dataField: InverseMultiSelectField = dField;
        let localFilter: InverseMultiSelectFilter;
        //is there an instance of one of these in the filters?
        let filterContainer: FilterContainer | undefined = this.filterContainers.find((filterContainer: FilterContainer) => {
          return filterContainer.dataField === dataField;
        });

        if (filterContainer != null) {
          localFilter = <InverseMultiSelectFilter>filterContainer.filter;
        } else {
          //we make a new one that defaults here to blank.
          localFilter = <InverseMultiSelectFilter>dataField.getFilterInstance();
        }

        //now we have to set all the keys to false in the searchValues that aren't true already.
        for (let key of dataField.selectOptions.keys()) {
          if (!localFilter.searchValues[key]) {
            localFilter.searchValues[key] = false;
          }
        }

        localFilter.isBlankOverride = false;
        dataRows = dataRows.filter((dataRow) => {
          return localFilter.passesFilter(dataRow);
        });
        delete localFilter.isBlankOverride;
      }
    });

    return dataRows;
  }

  /**
   * Adds the dataField as a selectable option to all of the showing GridWidgetFilterContainer objects.
   * @param dataField DataField object to add
   * @param skipId - integer - the id of the GridWidgetFilterContainer object to skipp adding the dataField to.
   */
  private addToFilterContainers(dataField: GridWidgetDataField, skipFilter?: FilterContainer): void {
    this.filterContainers.forEach((f: FilterContainer) => {
      if (f !== skipFilter) {
        f.availableFilterFields[dataField.id] = dataField;
      }
    });
  }

  private removeFromFilterContainers(dataField: GridWidgetDataField, skipFilter?: FilterContainer): void {
    this.filterContainers.forEach((f: FilterContainer) => {
      //We don't delete from a filter with skipId, or that is using the dataField
      if (f !== skipFilter && f.dataField.id !== dataField.id) {
        delete f.availableFilterFields[dataField.id];
      }
    });
  }

  /**
   * @returns Map<key, GridWidgetDataField>
   */
  private getAvailableFilterFields(): { [dataFieldId: string]: GridWidgetDataField } {
    //makes a clone of available filters
    let fields: { [key: string]: GridWidgetDataField } = this.filterFields.getMap();

    Object.keys(fields).forEach((key: string) => {
      let dataField = fields[key];
      if (dataField.filterDisabled === DisableFilterOption.advancedOnly ||
        dataField.filterDisabled === DisableFilterOption.both) {
        //The field is disabled so should not show up in the advanced filter options.
        delete fields[key];
      }
    });

    this.filterContainers.forEach((filterContainer: FilterContainer) => {
      //A filter container is already using this so remove it from the list of available filters.
      delete fields[filterContainer.dataField.id];
    });

    return fields;
  }
}
